import { 
    type CompositeFilterDescriptor, 
    type FilterDescriptor 
} from '@progress/kendo-data-query';
import {
    type GridColumnProps
} from '@progress/kendo-react-grid';
import { startOfDay, addDays } from 'date-fns';

function isDateEqualsFilter(filter: FilterDescriptor): boolean {
    return filter.operator === 'eq'
        && filter.value instanceof Date;
}

export function convertKendoTableDates<T>(data: T[], columns: GridColumnProps[]): T[] {
    return data.map(item => {
        const updatedItem = { ...item };
        // Convert all date field values from strings to Date objects 
        // to allow Kendo date filtering/sorting
        columns.forEach(column => {
            const fieldName = column.field as keyof T;
            if (column.filter === 'date' && updatedItem[fieldName]) {
                const dateValue = updatedItem[fieldName] as string;
                if (typeof dateValue === 'string' && !Number.isNaN(Date.parse(dateValue))) {
                    updatedItem[fieldName] = new Date(dateValue);
                }
            }
        });
        return updatedItem;
    });
}

// Recursively rewrite any date "equals" filters to include any time within the chosen date, not just 0:00:00
export function rewriteDateEqualsFilter(filter: CompositeFilterDescriptor | FilterDescriptor): CompositeFilterDescriptor | FilterDescriptor {
    const isCompositeFilter = ('filters' in filter);

    // Handle composite filter: rewrite children
    if (isCompositeFilter) {
        return {
            ...filter,
            filters: filter.filters.map(child => rewriteDateEqualsFilter(child))
        };
    }

    // Handle basic filter: rewrite if it's a date equals filter
    if (isDateEqualsFilter(filter)) {
        const start = startOfDay(filter.value as Date);
        const end = addDays(start, 1);

        // Replace the basic filter with a composite AND filter at the same position
        const composite: CompositeFilterDescriptor = {
            logic: 'and',
            filters: [
                { field: filter.field, operator: 'gte', value: start },
                { field: filter.field, operator: 'lt', value: end }
            ]
        };
        return composite;
    }

    return filter;
}
